import { BarChart } from '@mantine/charts';
import { Alert, Card, Group, Stack, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowDown, IconArrowUp, IconChartBarOff, IconInfoCircle, IconMinus } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ErrorMessage from '../../../components/ErrorMessage';
import Spinner from '../../../components/Spinner';
import Colors from '../../../constants/Colors';
import { daysAgoISODate, formatISODate, toISODate, todayISODate } from '../../../helpers/dateUtils';
import getErrorMessage from '../../../helpers/getErrorMessage';
import {
  EM_DASH,
  buildStageRows,
  describeComparisonAvailability,
  formatMonetaryScore,
  formatPercentChange,
  isPointAvailable,
  percentChangeTone,
} from '../../../helpers/rfmAnalysis';
import useFetch from '../../../helpers/useFetch';

const COMPARISON_PERIOD_DAYS = 30;

// The dates go out as bare YYYY-MM-DD, deliberately NOT through
// dateToStartOfDay/dateToEndOfDay like the other analytics routes. This endpoint
// parses its params as UTC and reduces them to a day label, so a local-midnight
// ISO datetime resolves to the previous day for anyone east of UTC — the user
// would ask for the 7th and silently get the 6th. Do not "fix" this back.
const rfmAnalysisDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, startDate, endDate) =>
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/rfmAnalysis/${startDate}/${endDate}`),
  300
);

const toneStyles = {
  positive: { color: 'var(--mantine-color-success-5)', Icon: IconArrowUp },
  negative: { color: 'var(--mantine-color-danger-5)', Icon: IconArrowDown },
  neutral: { color: 'var(--mantine-color-dimmed)', Icon: IconMinus },
};

const PercentChangeCell = ({ value }) => {
  const tone = percentChangeTone(value);
  const toneStyle = tone ? toneStyles[tone] : null;

  return (
    <Group gap={4} wrap="nowrap">
      {toneStyle && <toneStyle.Icon size={16} style={{ color: toneStyle.color }} />}
      <Text size="sm" c={toneStyle ? toneStyle.color : undefined}>
        {formatPercentChange(value)}
      </Text>
    </Group>
  );
};

const RfmAnalysisTab = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  const [startDate, setStartDate] = useState(daysAgoISODate(COMPARISON_PERIOD_DAYS));
  const [endDate, setEndDate] = useState(todayISODate());
  const [analysis, setAnalysis] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    let cancelled = false;
    setAnalysis(undefined);
    setError(undefined);

    rfmAnalysisDebounced(fetch, accountId, domainId, startDate, endDate)
      .then((data) => {
        if (!cancelled) setAnalysis(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, startDate, endDate]);

  const rows = useMemo(() => buildStageRows(analysis), [analysis]);

  const dateInputs = (
    <Group gap="sm" align="flex-end">
      <DatePickerInput
        label="Start date"
        value={startDate}
        onChange={(value) => setStartDate(toISODate(value) ?? startDate)}
        maxDate={endDate}
        popoverProps={{ withinPortal: true }}
      />
      <DatePickerInput
        label="End date"
        value={endDate}
        onChange={(value) => setEndDate(toISODate(value) ?? endDate)}
        minDate={startDate}
        maxDate={todayISODate()}
        popoverProps={{ withinPortal: true }}
      />
    </Group>
  );

  if (error) {
    return (
      <Stack gap="md" mt="md">
        {dateInputs}
        <ErrorMessage message={getErrorMessage(error)} />
      </Stack>
    );
  }

  if (analysis === undefined) {
    return (
      <Stack gap="md" mt="md">
        {dateInputs}
        <Spinner />
      </Stack>
    );
  }

  const comparisonNotice = describeComparisonAvailability(analysis);
  const previousAvailable = isPointAvailable(analysis.previous);
  // Label from the date the response actually carries per point, not the
  // picker state — the two agree today, but would silently diverge if the
  // endpoint ever resolved a requested day to the nearest snapshot it holds.
  const currentDate = analysis.current?.date || endDate;
  const previousDate = analysis.previous?.date || startDate;

  // Only points the API actually has numbers for get a series; an unavailable
  // one would otherwise draw as a row of zeroes.
  const series = [];
  if (isPointAvailable(analysis.current)) {
    series.push({ name: 'current', label: formatISODate(currentDate), color: Colors[0] });
  }
  if (previousAvailable) {
    series.push({ name: 'previous', label: formatISODate(previousDate), color: Colors[1] });
  }

  return (
    <Stack gap="md" mt="md">
      {dateInputs}

      {comparisonNotice && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          {comparisonNotice}
        </Alert>
      )}

      {rows.length === 0 ? (
        <Alert icon={<IconChartBarOff size={16} />} color="gray" variant="light" title="No RFM data yet">
          There is no lifecycle distribution recorded for either of the selected dates.
        </Alert>
      ) : (
        <>
          <Card padding="md" withBorder>
            <BarChart
              h={320}
              data={rows}
              dataKey="label"
              series={series}
              orientation="vertical"
              gridAxis="x"
              tickLine="x"
              withLegend
              legendProps={{ verticalAlign: 'top', horizontalAlign: 'right' }}
              yAxisProps={{ width: 120 }}
            />
          </Card>

          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Stage</Table.Th>
                <Table.Th>{formatISODate(currentDate)}</Table.Th>
                <Table.Th>{formatISODate(previousDate)}</Table.Th>
                <Table.Th>% change</Table.Th>
                <Table.Th>Avg. monetary score</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.stage}>
                  <Table.Td>{row.label}</Table.Td>
                  <Table.Td>{row.current === null ? EM_DASH : row.current.toLocaleString()}</Table.Td>
                  <Table.Td>{row.previous === null ? EM_DASH : row.previous.toLocaleString()}</Table.Td>
                  <Table.Td>
                    <PercentChangeCell value={row.percentChange} />
                  </Table.Td>
                  <Table.Td>{formatMonetaryScore(row.avgMonetaryScore)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Stack>
  );
};

export default RfmAnalysisTab;
